/**
 * DosimetryJobService — manages dosimetry job lifecycle per perfused volume.
 *
 * Handles:
 * - Running dosimetry.compute jobs via the API
 * - Polling job status until completion
 * - Fetching dose table metadata from completed job outputs
 * - Downloading and parsing TEP (simulated PET) volumes
 * - Setting dose tables and TEP volumes on the DosimetryStateService
 *
 * Moved from the useDosimetryJobs hook to the service layer so that
 * job orchestration logic lives outside React.
 */

import type { IJobResponse } from '@escloud/types';

import { DosimetryStateService } from './dosimetry-state-service';
import { parseDoseTable } from '../domain/dose-table';

import { PubSubService } from '@/visualization/core/pubsub-service';
import type { ServiceContext, ServiceRegistration } from '@/visualization/core/types';
import { parseMha } from '@/visualization/utils';

// ============================================================================
// API interface (narrow contract — injected via ServiceConfig)
// ============================================================================

export interface IDosimetryJobApi {
    jobs: {
        create(
            caseId: string,
            data: { jobKey: string; parameters?: Record<string, unknown> }
        ): Promise<IJobResponse>;
        getById(caseId: string, jobId: string): Promise<IJobResponse>;
        list(caseId: string): Promise<{ items: IJobResponse[] }>;
    };
    data: {
        listData(
            caseId: string,
            key?: string
        ): Promise<Array<{ id: string; type: string; metadata: Record<string, unknown> }>>;
        downloadData(caseId: string, dataId: string): Promise<ArrayBuffer>;
        deleteDataByKey(caseId: string, key: string): Promise<void>;
    };
}

// ============================================================================
// Types
// ============================================================================

export type DosimetryJobStatus = 'idle' | 'queued' | 'processing' | 'completed' | 'failed';

export interface JobStatusChangedPayload {
    pvId: string;
    status: DosimetryJobStatus;
    error?: string;
}

export interface LoadErrorPayload {
    message: string;
}

// ============================================================================
// Constants
// ============================================================================

const DOSIMETRY_JOB_KEY = 'dosimetry';
const POLL_INTERVAL_MS = 3000;
const MAX_CONSECUTIVE_POLL_ERRORS = 3;

// ============================================================================
// Service
// ============================================================================

export class DosimetryJobService extends PubSubService {
    static readonly SERVICE_NAME = 'DosimetryJobService';

    static readonly REGISTRATION: ServiceRegistration = {
        name: DosimetryJobService.SERVICE_NAME,
        factory: (context: ServiceContext) => new DosimetryJobService(context),
    };

    readonly EVENTS = {
        JOB_STATUS_CHANGED: 'dosimetry-jobs:status:changed',
        LOAD_ERROR: 'dosimetry-jobs:load:error',
    } as const;

    private readonly api: IDosimetryJobApi;
    private readonly context: ServiceContext;

    /** Job status per perfused volume ID */
    private _jobStatus = new Map<string, DosimetryJobStatus>();
    /** Error message per perfused volume ID */
    private _jobErrors = new Map<string, string>();
    /** jobId -> pvId mapping for polling */
    private _jobToPv = new Map<string, string>();
    /** Active poll timers keyed by jobId */
    private _pollTimers = new Map<string, ReturnType<typeof setInterval>>();
    /** PV removal subscription cleanup */
    private _pvRemovalUnsub: (() => void) | null = null;

    constructor(context: ServiceContext) {
        super();
        this.context = context;
        this.api = context.config?.api as IDosimetryJobApi;
    }

    // ========================================================================
    // Public API
    // ========================================================================

    getJobStatus(): ReadonlyMap<string, DosimetryJobStatus> {
        return this._jobStatus;
    }

    getJobErrors(): ReadonlyMap<string, string> {
        return this._jobErrors;
    }

    getHasRunningJobs(): boolean {
        for (const status of this._jobStatus.values()) {
            if (status === 'queued' || status === 'processing') return true;
        }
        return false;
    }

    /**
     * Run dosimetry for a specific perfused volume.
     */
    async runDosimetry(
        caseId: string,
        pvId: string,
        segmentIndices: number[],
        tumorIndices: number[]
    ): Promise<void> {
        this.setStatusForPv(pvId, 'queued');

        try {
            const job = await this.api.jobs.create(caseId, {
                jobKey: DOSIMETRY_JOB_KEY,
                parameters: {
                    name: pvId,
                    perfused_segments: segmentIndices,
                    perfused_tumors: tumorIndices,
                },
            });
            this._jobToPv.set(job.id, pvId);
            this.startPolling(caseId, job.id, pvId);
        } catch (error) {
            this.setStatusForPv(pvId, 'failed');
            this.setErrorForPv(pvId, error instanceof Error ? error.message : 'Failed to create job');
        }
    }

    /**
     * Load existing jobs from the server and resume polling for incomplete ones.
     */
    async loadExistingJobs(caseId: string): Promise<void> {
        try {
            const response = await this.api.jobs.list(caseId);
            const dosimetryJobs = response.items.filter((j: IJobResponse) => j.jobKey === DOSIMETRY_JOB_KEY);

            const fetchPromises: Promise<void>[] = [];

            for (const job of dosimetryJobs) {
                const pvId = (job.parameters as { name?: string })?.name;
                if (!pvId) continue;

                if (job.status === 'completed') {
                    this.setStatusForPv(pvId, 'completed');
                    fetchPromises.push(this.fetchJobResults(caseId, pvId));
                } else if (job.status === 'failed') {
                    this.setStatusForPv(pvId, 'failed');
                    this.setErrorForPv(pvId, job.error ?? 'Job failed');
                } else if (job.status === 'queued' || job.status === 'processing') {
                    this.setStatusForPv(pvId, job.status as DosimetryJobStatus);
                    this._jobToPv.set(job.id, pvId);
                    this.startPolling(caseId, job.id, pvId);
                }
            }

            await Promise.all(fetchPromises);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to load existing job results';
            console.error('[DosimetryJobService] Failed to load existing jobs:', error);
            this.broadcast(this.EVENTS.LOAD_ERROR, { message });
        }
    }

    /**
     * Clean up job state and server-side data when a perfused volume is deleted.
     */
    async cleanupPv(caseId: string, pvId: string): Promise<void> {
        // Stop any active polling for this PV
        for (const [jobId, mappedPvId] of this._jobToPv.entries()) {
            if (mappedPvId === pvId) {
                const timer = this._pollTimers.get(jobId);
                if (timer) {
                    clearInterval(timer);
                    this._pollTimers.delete(jobId);
                }
                this._jobToPv.delete(jobId);
            }
        }

        // Clear status and error entries
        const hadStatus = this._jobStatus.delete(pvId);
        this._jobErrors.delete(pvId);

        // Remove TEP volume from state service
        try {
            const stateService = this.context.services.getService<DosimetryStateService>(
                DosimetryStateService.SERVICE_NAME
            );
            stateService.removeTepVolume(pvId);
        } catch (error) {
            console.warn('[DosimetryJobService] Could not remove TEP volume during cleanup:', error);
        }

        // Delete server-side dosimetry result data
        const dataKey = `dosimetry-${pvId}`;
        try {
            await this.api.data.deleteDataByKey(caseId, dataKey);
        } catch (error) {
            console.error(`[DosimetryJobService] Failed to delete server data for PV "${pvId}":`, error);
        }

        if (hadStatus) {
            this.broadcast(this.EVENTS.JOB_STATUS_CHANGED, { pvId, status: 'idle' });
        }
    }

    /**
     * Subscribe to PV removal events for cascade cleanup.
     * Call this once after mount with the current caseId.
     */
    subscribeToPvRemovals(caseId: string): void {
        this.unsubscribeFromPvRemovals();

        try {
            const stateService = this.context.services.getService<DosimetryStateService>(
                DosimetryStateService.SERVICE_NAME
            );
            this._pvRemovalUnsub = stateService.subscribe(
                stateService.EVENTS.PERFUSED_VOLUME_REMOVED,
                (payload: unknown) => {
                    const { id } = payload as { id: string };
                    this.cleanupPv(caseId, id);
                }
            );
        } catch (error) {
            console.error('[DosimetryJobService] Failed to subscribe to PV deletion events:', error);
        }
    }

    unsubscribeFromPvRemovals(): void {
        this._pvRemovalUnsub?.();
        this._pvRemovalUnsub = null;
    }

    // ========================================================================
    // Reset / cleanup
    // ========================================================================

    override reset(): void {
        this.unsubscribeFromPvRemovals();
        for (const timer of this._pollTimers.values()) {
            clearInterval(timer);
        }
        this._pollTimers.clear();
        this._jobToPv.clear();
        this._jobStatus.clear();
        this._jobErrors.clear();
        super.reset();
    }

    // ========================================================================
    // Private helpers
    // ========================================================================

    private setStatusForPv(pvId: string, status: DosimetryJobStatus): void {
        this._jobStatus.set(pvId, status);
        const error = this._jobErrors.get(pvId);
        this.broadcast(this.EVENTS.JOB_STATUS_CHANGED, { pvId, status, error });
    }

    private setErrorForPv(pvId: string, error: string): void {
        this._jobErrors.set(pvId, error);
        const status = this._jobStatus.get(pvId) ?? 'failed';
        this.broadcast(this.EVENTS.JOB_STATUS_CHANGED, { pvId, status, error });
    }

    /**
     * Fetch all job results for a completed perfused volume.
     */
    private async fetchJobResults(caseId: string, pvId: string): Promise<void> {
        const dataKey = `dosimetry-${pvId}`;
        let dataItems;
        try {
            dataItems = await this.api.data.listData(caseId, dataKey);
        } catch (error) {
            console.error(`[DosimetryJobService] Failed to list data for PV "${pvId}":`, error);
            this.setErrorForPv(pvId, 'Failed to retrieve dosimetry results from server.');
            return;
        }

        if (dataItems.length === 0) return;

        const stateService = this.context.services.getService<DosimetryStateService>(
            DosimetryStateService.SERVICE_NAME
        );

        // Process dose table metadata
        const metadata = dataItems[0].metadata;
        if (metadata?.dosimetry) {
            try {
                const doseTable = parseDoseTable(
                    metadata.dosimetry as Record<string, { unit: string; value: number }>
                );
                stateService.setDoseTable(pvId, doseTable);
            } catch (error) {
                console.error(`[DosimetryJobService] Failed to parse dose table for PV "${pvId}":`, error);
            }
        }

        // Download and parse TEP volume
        const tepItem = dataItems.find((item) => item.type === 'VOLUME' || item.type === 'DOSIMETRY');
        if (!tepItem) return;

        try {
            const buffer = await this.api.data.downloadData(caseId, tepItem.id);
            const imageData = await parseMha(buffer);
            stateService.setTepVolume(pvId, imageData);
        } catch (error) {
            console.error(
                `[DosimetryJobService] Failed to download/parse TEP volume for PV "${pvId}":`,
                error
            );
            this.setErrorForPv(
                pvId,
                'Failed to load TEP image. The dosimetry visualization may be incomplete.'
            );
        }
    }

    private startPolling(caseId: string, jobId: string, pvId: string): void {
        let consecutivePollErrors = 0;

        const timer = setInterval(async () => {
            try {
                const job = await this.api.jobs.getById(caseId, jobId);
                consecutivePollErrors = 0;
                if (job.status === 'completed') {
                    clearInterval(timer);
                    this._pollTimers.delete(jobId);
                    this.setStatusForPv(pvId, 'completed');
                    await this.fetchJobResults(caseId, pvId);
                } else if (job.status === 'failed') {
                    clearInterval(timer);
                    this._pollTimers.delete(jobId);
                    this.setStatusForPv(pvId, 'failed');
                    this.setErrorForPv(pvId, job.error ?? 'Job failed');
                } else {
                    this.setStatusForPv(pvId, job.status as DosimetryJobStatus);
                }
            } catch (error) {
                consecutivePollErrors++;
                console.error(
                    `[DosimetryJobService] Poll error for job ${jobId} (attempt ${consecutivePollErrors}):`,
                    error
                );
                if (consecutivePollErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
                    clearInterval(timer);
                    this._pollTimers.delete(jobId);
                    this.setStatusForPv(pvId, 'failed');
                    this.setErrorForPv(pvId, 'Lost connection to server. Please reload the page.');
                }
            }
        }, POLL_INTERVAL_MS);

        this._pollTimers.set(jobId, timer);
    }
}
